import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	advancesByReporting: [],
	advancesById: [],
	advanceStatusMaster: [],
	total: 0,
	advance: null,
	loading: false,
	statusLoading: false,
	error: null,
};

const extractMessages = (data) => {
	if (Array.isArray(data)) {
		return data;
	}
	if (Array.isArray(data?.messages)) {
		return data.messages;
	}
	if (Array.isArray(data?.result)) {
		return data.result;
	}
	return null;
};

const showSuccessMessages = (data, fallbackMessage) => {
	const messages = extractMessages(data);
	if (messages && messages.length) {
		messages.forEach((msg) => {
			if (msg?.status === "success") {
				toast.success(msg?.message || fallbackMessage || "Operation successful");
			} else if (msg?.status === "error") {
				toast.error(msg?.message || "Operation failed");
			}
		});
	} else if (fallbackMessage) {
		toast.success(fallbackMessage);
	}
};

const showErrorMessages = (errorData, fallbackMessage) => {
	const messages = extractMessages(errorData);
	if (messages && messages.length) {
		messages.forEach((msg) => toast.error(msg?.message || fallbackMessage));
	} else {
		toast.error(errorData?.message || fallbackMessage);
	}
};

const normalizeNumber = (value, fallback = null) => {
	if (value === undefined || value === null || value === "") {
		return fallback;
	}
	const numeric = Number(value);
	return Number.isNaN(numeric) ? fallback : numeric;
};

const buildAdvancePayload = (payload = {}) => ({
	AdvancePaymentId: normalizeNumber(payload.AdvancePaymentId, 0),
	EmployeeId: normalizeNumber(payload.EmployeeId, 0),
	Amount: normalizeNumber(payload.Amount, 0),
	ApprovedAmount: normalizeNumber(payload.ApprovedAmount, null),
	Installments: normalizeNumber(payload.Installments, 1),
	RequestDate: payload.RequestDate ?? null,
	Reason: payload.Reason ?? null,
	Remarks: payload.Remarks ?? null,
	StatusId: normalizeNumber(payload.StatusId, 0),
	DataStatus: normalizeNumber(payload.DataStatus, 1),
});

// Load all advance payment requests (admin view)
export const loadAdvancePayments = createAsyncThunk(
	"advancePayment/loadAll",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("advancePayments/AdvancePaymentDetails");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load advance payments",
				}
			);
		}
	}
);

// Load advance payment requests raised by employees who report to this manager
export const loadAdvancePaymentsByReporting = createAsyncThunk(
	"advancePayment/loadByReporting",
	async (employeeId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`advancePayments/AdvancePaymentGetByReporting?EmployeeId=${encodeURIComponent(
					employeeId
				)}`
			);
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load advance payments by reporting",
				}
			);
		}
	}
);

// Load advance payment requests raised by a single employee (self)
export const loadAdvancePaymentsById = createAsyncThunk(
	"advancePayment/loadById",
	async (employeeId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`advancePayments/AdvancePaymentById?EmployeeId=${encodeURIComponent(employeeId)}`
			);
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load advance payments by employee",
				}
			);
		}
	}
);

export const loadAdvancePaymentStatusMaster = createAsyncThunk(
	"advancePayment/loadStatusMaster",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("advancePayments/AdvancePaymentStatusMaster");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load advance payment status master",
				}
			);
		}
	}
);

// Accept / reject an advance payment request
export const advancePaymentUpdateAction = createAsyncThunk(
	"advancePayment/updateAction",
	async ({ AdvancePaymentId, StatusId, ApprovedAmount }, { rejectWithValue }) => {
		try {
			const response = await axios.post("advancePayments/AdvancePaymentUpdateAction", {
				AdvancePaymentId: normalizeNumber(AdvancePaymentId, 0),
				StatusId: normalizeNumber(StatusId, 0),
				ApprovedAmount: normalizeNumber(ApprovedAmount, null),
			});
			showSuccessMessages(
				response?.data,
				StatusId === 1 ? "Advance payment approved" : "Advance payment rejected"
			);
			return response?.data;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(
				errorData,
				StatusId === 1
					? "Failed to approve advance payment"
					: "Failed to reject advance payment"
			);
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Advance payment action failed",
				}
			);
		}
	}
);

// Create / update / delete an advance payment request
export const saveAdvancePayment = createAsyncThunk(
	"advancePayment/save",
	async (payload, { rejectWithValue }) => {
		const requestBody = buildAdvancePayload(payload);
		try {
			const response = await axios.post(
				"advancePayments/AdvancePaymentDetailsSetup",
				requestBody
			);
			const responseData = response?.data;
			const isDelete = requestBody.DataStatus === 2;
			showSuccessMessages(
				responseData,
				isDelete
					? "Advance payment deleted"
					: requestBody.AdvancePaymentId && requestBody.AdvancePaymentId !== 0
					? "Advance payment updated"
					: "Advance payment request submitted"
			);
			return responseData;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to save advance payment");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to save advance payment",
				}
			);
		}
	}
);

const advancePaymentSlice = createSlice({
	name: "advancePayment",
	initialState,
	reducers: {
		clearAdvancePayment: (state) => {
			state.advance = null;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadAdvancePayments.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadAdvancePayments.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
				state.total = state.list.length;
			})
			.addCase(loadAdvancePayments.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load advance payments";
			})
			.addCase(loadAdvancePaymentsByReporting.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadAdvancePaymentsByReporting.fulfilled, (state, action) => {
				state.loading = false;
				state.advancesByReporting = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadAdvancePaymentsByReporting.rejected, (state, action) => {
				state.loading = false;
				state.advancesByReporting = [];
				state.error =
					action.payload?.message || "Unable to load advance payments by reporting";
			})
			.addCase(loadAdvancePaymentsById.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadAdvancePaymentsById.fulfilled, (state, action) => {
				state.loading = false;
				state.advancesById = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadAdvancePaymentsById.rejected, (state, action) => {
				state.loading = false;
				state.advancesById = [];
				state.error =
					action.payload?.message || "Unable to load advance payments by employee";
			})
			.addCase(saveAdvancePayment.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveAdvancePayment.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(saveAdvancePayment.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save advance payment";
			})
			.addCase(advancePaymentUpdateAction.pending, (state) => {
				state.loading = true;
			})
			.addCase(advancePaymentUpdateAction.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(advancePaymentUpdateAction.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Advance payment action failed";
			})
			.addCase(loadAdvancePaymentStatusMaster.pending, (state) => {
				state.statusLoading = true;
			})
			.addCase(loadAdvancePaymentStatusMaster.fulfilled, (state, action) => {
				state.statusLoading = false;
				state.advanceStatusMaster = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadAdvancePaymentStatusMaster.rejected, (state, action) => {
				state.statusLoading = false;
				state.advanceStatusMaster = [];
				state.error =
					action.payload?.message || "Unable to load advance payment status master";
			});
	},
});

export default advancePaymentSlice.reducer;
export const { clearAdvancePayment } = advancePaymentSlice.actions;
